import React, { Fragment, useState, useContext, useEffect } from 'react';
import { useDispatch, useSelector, connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import get from 'lodash/get';
import { accountIcons } from 'images';
import warning from 'images/Warning.png';
import { createStructuredSelector } from 'reselect';
import {
  BlockButton,
  ButtonGroup,
  Button,
  Note,
  Icon,
  Div,
  Span,
  H1,
} from 'components/Ui';
import { translateData } from 'utils/translation';
import { isPastDeliveryCutOffDateTime } from 'utils/common';
import NoUpcomingDeliveriesDialog from 'components/Dialogs/NoUpcomingDeliveriesDialog/Loadable';
import { useIsVertPhone } from 'utils/hooks';
import {
  loadRecurringOrder as loadRecurringOrderRequest,
  addExtraDelivery as addExtraDeliveryRequest,
  updateNextDelivery as updateNextDeliveryRequest,
  showSkipDialog as showSkipDialogRequest,
} from 'containers/PrimoAccount/actions';
import {
  fetchDeliveryNotes,
  fetchCustomerNotes,
} from 'containers/PrimoProfile/actions';

import {
  ProductListItem,
  CTABanner,
  NotificationBanner,
} from 'components/ContentfulFields/ProductCard';
import {
  selectRecurringOrder,
  selectCalendarDates,
  selectNextDelivery,
  selectDeliveryAfterSkip,
  selectSelectedServiceLocation,
  selectIsFirstDelivery,
  selectBestDeliveryDates,
} from 'containers/PrimoAccount/selectors';
import { ProductListContext } from 'containers/PrimoProducts/ProductListPage/ProductListPage';
import { GTM_TRACKING_TAGS } from 'utils/constants';
import { dataLayerPush } from 'utils/tracking';
import { EDIT_CUSTOMER_NOTES } from 'containers/PrimoProfile/BillingInformation/constants';
import { showDrawer } from 'containers/DrawerManager/actions';
import CustomerNotesDrawer from 'containers/PrimoProfile/DeliveryInformation/CustomerNotesDrawer';
import { updateRecurringOrder } from '../../actions';
import styles from './styles.scss';
import commonStyles from '../styles.scss';
import ConfirmationDrawer from './ConfirmationDrawer';
import ProcessingModal from './ProcessingModal';
import SuccessModal from './SuccessModal';
import ChangeSuccessfulModal from './ChangeSuccessfulModal';

const formatLocalDateTime = (date) =>
  moment.utc(date).format('ha [ET on] MM/DD/YY');
const formatDate = (date) => moment(date).format('MMM Do YYYY');

const YourOrder = ({
  nextDelivery: defaultDelivery,
  loadRecurringOrder,
  serviceLocation,
  deliveryAfterSkip,
  updateNextDelivery,
  showSkipDialog,
  history,
  isFirstDelivery,
  bestDeliveryDates,
  addExtraDelivery,
  getDeliveryNotes,
  getCustomerNotes,
}) => {
  useEffect(() => {
    getDeliveryNotes();
    getCustomerNotes();
  }, []);

  const isVertPhone = useIsVertPhone();
  const dispatch = useDispatch();
  const order = useSelector(selectRecurringOrder());
  const [showConfirmation, changeShowConfirmation] = useState(false);
  const [
    showNoUpcomingDeliveriesDialog,
    setShowNoUpcomingDeliveriesDialog,
  ] = useState(false);
  const handleHide = () => changeShowConfirmation(false);

  const [nextDelivery, changeDelivery] = useState(defaultDelivery);
  const isSameDay = moment().isSame(nextDelivery.date);

  const startEditing = () => loadRecurringOrder();

  const setNoUpcomingDeliveriesDialogOpen = () =>
    setShowNoUpcomingDeliveriesDialog(true);

  const setNoUpcomingDeliveriesDialogClosed = () =>
    setShowNoUpcomingDeliveriesDialog(false);

  const addToRecurringOrder = (product) => {
    const recurringItems = [
      ...order,
      { ...product, recurringDeliveryId: serviceLocation.recurringDeliveryId },
    ];
    dispatch(
      updateRecurringOrder(
        recurringItems.map((item) => ({
          itemId: item.itemId,
          itemNumber: item.itemNumber,
          quantity: item.quantity,
          recurringDeliveryId: item.recurringDeliveryId,
          deliveryFrequency: item.deliveryFrequency
            ? item.deliveryFrequency
            : 'Every Delivery',
        })),
        true,
      ),
    );

    const {
      itemNumber,
      name,
      brand,
      category,
      price,
      size,
      quantity,
    } = product;

    dataLayerPush(
      'SelfServe',
      {
        event: 'add-to-recurring-order',
        section: 'next delivery',
        id: itemNumber,
        name,
        brand: brand || '',
        category,
        variant: size,
        price,
        quantity,
        position: 1,
      },
      'dlS22',
      'PUSH_ADD_TO_RECURRING_ORDER',
    );
  };

  const context = useContext(ProductListContext);

  const createExtraDelivery = () => {
    if (bestDeliveryDates && bestDeliveryDates.length > 0) {
      addExtraDelivery();
    } else {
      setNoUpcomingDeliveriesDialogOpen();
    }
  };

  const handleShowConfirmation = () => {
    if (!showConfirmation) {
      changeShowConfirmation(true);
    }
  };

  const handleShop = () => {
    history.push(
      `/account/products/list?serviceLocationId=${serviceLocation.id}`,
    );
  };

  const handleShowCustomerNotes = () => {
    dispatch(showDrawer(EDIT_CUSTOMER_NOTES));
  };

  const areSameItem = (item1, item2) =>
    `${item1.scr}|${item1.itemId}|${item1.scrOd}` ===
    `${item2.scr}|${item2.itemId}|${item2.scrOd}`;

  const handleConfirm = () => {
    const removedItems = [];
    const changedItems = [];

    const nextDeliveryItems = defaultDelivery.items.map((defaultItem) => {
      const item = defaultItem;
      /* eslint-disable-next-line prefer-destructuring */
      let quantity = defaultItem.quantity;
      let deliveryAction = 'NO_CHANGE';
      const deliveryItemFound = nextDelivery.items.find((deliveryItem) =>
        areSameItem(deliveryItem, defaultItem),
      );
      if (deliveryItemFound && deliveryItemFound.quantity > 0) {
        if (defaultItem.quantity !== deliveryItemFound.quantity) {
          /* eslint-disable-next-line prefer-destructuring */
          quantity = deliveryItemFound.quantity;
          deliveryAction = 'CHANGED';
          deliveryItemFound['net-change'] =
            deliveryItemFound.quantity - defaultItem.quantity;
          const changedItem = {
            ...deliveryItemFound,
            id: deliveryItemFound.itemNumber,
            variant: deliveryItemFound.size,
          };
          delete changedItem.image;
          delete changedItem.images;
          changedItems.push(changedItem);
        }
      } else {
        deliveryAction = 'REMOVED';
        const removedItem = {
          ...defaultItem,
          id: defaultItem.itemNumber,
          variant: defaultItem.size,
        };
        delete removedItem.image;
        delete removedItem.images;
        removedItems.push(removedItem);
      }
      return { ...item, quantity, deliveryAction };
    });

    const dateDelivery = moment(
      get(serviceLocation, 'nextDeliveryDate'),
    ).format('YYYY-MM-DD');

    updateNextDelivery(
      nextDeliveryItems.map((item) => ({
        deliveryAction: item.deliveryAction,
        deliveryType: item.deliveryType,
        instanceId: item.instanceId,
        itemId: item.itemId,
        itemNumber: item.itemNumber,
        description: item.longDescription || item.description,
        quantity: item.quantity,
        itemType: item.itemType,
        lineNumber: item.lineNumber,
        orderNumber: item.orderNumber,
        orderReason: item.orderReason,
        orderStatus: item.orderStatus,
        orderType: item.orderType,
        priceChange: item.priceChange,
        removable: item.removable,
        src: item.src,
        srcId: item.srcId,
        serviceType: item.serviceType,
        price: item.price && item.price.toFixed(2),
        floorPrice: item.floorPrice && item.floorPrice.toFixed(2),
        lineTotal: item.lineTotal && item.lineTotal.toFixed(2),
        listPrice: item.listPrice && item.listPrice.toFixed(2),
      })),
      dateDelivery,
      updateNextDeliverySuccessCallback,
    );

    if (context.selectedProductRef && context.selectedProductRef.current) {
      context.selectedProductRef.current.focus();
    }

    if (changedItems.length) {
      dataLayerPush(
        'SelfServe',
        {
          event: 'quantity-change',
          section: 'next delivery',
          products: changedItems,
        },
        'dlS23',
        'PUSH_UPDATE_NEXT_DELIVERY_SUCCESS',
      );
    }

    if (removedItems.length) {
      dataLayerPush(
        'SelfServe',
        {
          event: 'delete',
          section: 'next delivery',
          products: removedItems,
        },
        'dlS24',
        'PUSH_UPDATE_NEXT_DELIVERY_SUCCESS',
      );
    }
  };

  const handleShowSkipDialog = () => {
    const formatDateForDataLayer = (date) => moment(date).format('YYYY-MM-DD');
    const date = formatDateForDataLayer(nextDelivery.date);
    dataLayerPush(
      'SelfServe',
      {
        event: 'skip-delivery',
        date,
      },
      'dlS25',
    );
    showSkipDialog();
  };

  const handleCancel = () => {
    changeDelivery(defaultDelivery);
    handleHide();
  };

  const updateNextDeliverySuccessCallback = () => {
    // Since we no longer hide the confirmation panel (change needed to process errors),
    // we now need to hide the confirmation panel on success of updateNextDelivery
    handleHide();
  };

  const onDelete = (deletedProduct, index) => {
    handleShowConfirmation();

    const newItems = nextDelivery.items.filter(
      (product, i) =>
        i !== index || product.itemNumber !== deletedProduct.itemNumber,
    );
    changeDelivery({ ...nextDelivery, items: newItems });
  };

  const handleQuantityChange = (product, newCount, index) => {
    handleShowConfirmation();

    const newItems = nextDelivery.items.map((orderedItem, i) => ({
      ...orderedItem,
      quantity:
        index === i && orderedItem.itemNumber === product.itemNumber
          ? newCount
          : orderedItem.quantity,
    }));
    changeDelivery({ ...nextDelivery, items: newItems });
  };

  const isPastCutOff = isPastDeliveryCutOffDateTime(serviceLocation);

  const ctaBanner =
    isFirstDelivery || isPastCutOff ? null : (
      <div
        data-tracking={GTM_TRACKING_TAGS['Add to your order']}
        className={styles.addButton}
      >
        <CTABanner
          title="Add to your order"
          text="Browse products to add to your order."
          ctaText="Shop"
          icon={
            <img src={accountIcons.Plus} alt="" className={styles.ctaIcon} />
          }
          onAction={handleShop}
        />
      </div>
    );

  const editDeliveryInstructionsBanner = (
    <div
      data-tracking={GTM_TRACKING_TAGS['Manage your delivery instructions']}
      className={styles.addButton}
    >
      <CTABanner
        title="Manage your delivery instructions"
        text=""
        ctaText="Edit Instructions"
        icon={<img src={accountIcons.Plus} alt="" className={styles.ctaIcon} />}
        onAction={handleShowCustomerNotes}
      />
    </div>
  );

  const bottlePickupBanner = nextDelivery.bottlePickupRequested ? (
    <div>
      <NotificationBanner
        title="Bottle pickup requested"
        text="You requested empty bottles to be picked up. If you did not request this, please contact customer service."
        image={<img src={accountIcons.BottlePickup} alt="" />}
      />
    </div>
  ) : null;

  const equipmentServiceBanner = nextDelivery.equipmentServiceRequested ? (
    <div>
      <NotificationBanner
        title="Equipment Service requested"
        text="You have an equipment service, pickup or exchange scheduled. Please make sure someone is home and the area designated for your equipment is clear."
        image={<img src={accountIcons.EquipmentService} alt="" />}
      />
    </div>
  ) : null;

  const cutoffMessage = isPastCutOff
    ? `${translateData('This order cannot be changed at this time')}.`
    : `${translateData(
        'This order may be modified until',
      )} ${formatLocalDateTime(
        get(serviceLocation, 'deliveryCutOffDateTime'),
      )}.`;
  const cutoffIcon = isPastCutOff ? (
    <img src={warning} alt="" />
  ) : (
    <img src={accountIcons.Cog} alt="" />
  );

  return (
    <Fragment>
      <div className={styles.manageRecurringOrderContainer}>
        <div className={styles.content}>
          <Icon className={styles.icon} icon="cog" />
          <Div className={styles.text}>Need to edit your recurring order?</Div>
          <div className={styles.manageButton}>
            <Button
              className={styles.button}
              onClick={startEditing}
              text="Manage recurring order"
              ariaLabel="manage your recurring order"
              link
            />
          </div>
        </div>
      </div>
      <div className={commonStyles.blockContainer}>
        <div className={commonStyles.header}>
          <div className={commonStyles.titleContainer}>
            <H1 className={commonStyles.title}>Your next delivery</H1>
            <Span>Here’s what is coming on your next delivery.</Span>
          </div>
          {!isFirstDelivery && isPastCutOff && nextDelivery.editable && (
            <Note className={styles.note}>
              {cutoffIcon}
              {cutoffMessage}
            </Note>
          )}
          {isFirstDelivery && (
            <Note className={styles.note}>
              <img src={accountIcons.Not} alt="" />
              If this is your first order, you cannot modify your order.
              <br />
              Please call customer service if you have any issues.
            </Note>
          )}
        </div>
        <div className={styles.cartItems}>
          <div className={styles.productWrapper}>
            {ctaBanner}
            {nextDelivery.items.map((item, i) => (
              <ProductListItem
                onQuantityChange={
                  !isFirstDelivery && !isPastCutOff && nextDelivery.editable
                    ? handleQuantityChange
                    : null
                }
                onDelete={
                  !isFirstDelivery &&
                  !isPastCutOff &&
                  nextDelivery.editable &&
                  item.removable
                    ? onDelete
                    : null
                }
                key={`${item.itemNumber}-${i}`} // eslint-disable-line react/no-array-index-key
                index={i}
                product={item}
                onAction={startEditing}
                onAddToRecurringOrder={addToRecurringOrder}
                usePropsCountValue
                yourOrderView
                disableDelete={nextDelivery.items.length === 1}
              />
            ))}
            {bottlePickupBanner}
            {equipmentServiceBanner}
            {editDeliveryInstructionsBanner}
          </div>
        </div>

        {!isFirstDelivery && (
          <ButtonGroup fullWidth className={styles.buttonGroup}>
            <BlockButton
              data-tracking={GTM_TRACKING_TAGS['Skip this delivery']}
              disabled={isSameDay || isPastCutOff}
              icon={
                <img
                  className={styles.buttonGroupIcon}
                  src={accountIcons.SkipIcon}
                  alt=""
                />
              }
              title="Skip this delivery"
              text={
                <Div className={styles.buttonGroupText}>
                  Going on vacation? No problem. All future deliveries will
                  continue according to their normal schedule.
                </Div>
              }
              note={
                deliveryAfterSkip ? (
                  <span className={styles.skipNote}>
                    <img src={accountIcons.Calendar} alt="" />
                    <Fragment>
                      <Span>If skipped, your next delivery would be</Span>{' '}
                      {formatDate(deliveryAfterSkip.date)}
                    </Fragment>
                  </span>
                ) : (
                  ''
                )
              }
              onClick={handleShowSkipDialog}
            />
            <BlockButton
              disabled={isPastCutOff}
              onClick={createExtraDelivery}
              icon={
                <img
                  className={styles.buttonGroupIcon}
                  src={accountIcons.AddItem}
                  alt=""
                />
              }
              data-tracking={GTM_TRACKING_TAGS['Add an extra delivery']}
              title="Add an extra delivery"
              text={
                <Div className={styles.buttonGroupText}>
                  Need something else? Create a new separate order to arrive
                  before your normally scheduled delivery. We got you covered.
                </Div>
              }
            />
            <BlockButton
              onClick={startEditing}
              icon={
                <img
                  className={styles.buttonGroupIcon}
                  src={accountIcons.ManageIcon}
                  alt=""
                />
              }
              data-tracking={GTM_TRACKING_TAGS['Manage recurring order']}
              title="Manage recurring order"
              text={
                <Div className={styles.buttonGroupText}>
                  Feeling extra thirsty lately? Edit the products that are part
                  of your recurring deliveries. Simply set it and forget it!
                </Div>
              }
            />
          </ButtonGroup>
        )}
      </div>
      <ConfirmationDrawer
        isVertPhone={isVertPhone}
        open={showConfirmation}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <NoUpcomingDeliveriesDialog
        open={showNoUpcomingDeliveriesDialog}
        setStatus={setNoUpcomingDeliveriesDialogClosed}
        dateDelivery={moment(get(serviceLocation, 'nextDeliveryDate')).format(
          'MMMM Do YYYY',
        )}
      />
      <ProcessingModal />
      <SuccessModal />
      <ChangeSuccessfulModal message="This change will be made to all future recurring orders." />
      <CustomerNotesDrawer />
    </Fragment>
  );
};

YourOrder.propTypes = {
  nextDelivery: PropTypes.object,
  deliveryAfterSkip: PropTypes.object,
  loadRecurringOrder: PropTypes.func,
  updateNextDelivery: PropTypes.func,
  serviceLocation: PropTypes.object,
  showSkipDialog: PropTypes.func,
  isFirstDelivery: PropTypes.bool,
  history: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
  nextDelivery: selectNextDelivery(),
  calendarDates: selectCalendarDates(),
  deliveryAfterSkip: selectDeliveryAfterSkip(),
  serviceLocation: selectSelectedServiceLocation(),
  isFirstDelivery: selectIsFirstDelivery(),
  bestDeliveryDates: selectBestDeliveryDates(),
});

const mapDispatchToProps = {
  loadRecurringOrder: loadRecurringOrderRequest,
  addExtraDelivery: addExtraDeliveryRequest,
  updateNextDelivery: updateNextDeliveryRequest,
  showSkipDialog: showSkipDialogRequest,
  getDeliveryNotes: fetchDeliveryNotes,
  getCustomerNotes: fetchCustomerNotes,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(YourOrder),
);
